/**
 * Transform HANDLE geometry — one owner for where the on-canvas rotate/resize
 * affordances sit, what the cursor has to hit to grab them, and how a grabbed
 * handle turns into a new box (#990, epic #781).
 *
 * The leather transform (#971) and construction's rotate/resize were built as
 * two systems that happened to share their *math*: `constructionResize`'s box
 * helpers and `rotatePathData` on the model side, `buildTransformOverlay` on the
 * render side. What stayed forked was everything in between — each controller
 * hand-wrote its own "un-rotate the cursor, is it near the knob, is it near a
 * corner, else an edge" pick. So the same affordance had three definitions of
 * where the rotation knob is (`Scene.LINE_ROTATION_HANDLE_OFFSET_MM` for the
 * drawn stem, `RotationController.ROTATION_HANDLE_OFFSET_MM` for construction's
 * grab, `TransformController.KNOB_OFFSET_MM` for leather's) and the drift showed
 * up as the bugs #990 lists: a stem that moved with zoom on one side only, a
 * pivot that snapped to center on one side only.
 *
 * This module is the hit-side twin of `buildTransformOverlay`: the renderer
 * decides how the handles LOOK, this decides where they ARE. Both read the same
 * constants, so a handle can no longer be drawn in one place and grabbed in
 * another.
 *
 * Everything here is pure and paper-free. Callers keep what is genuinely theirs:
 * the *tuning* (grab radii differ — see `HandlePick`), and the object-specific
 * constraints layered on top of `applyResizeStep` (construction's grid snap and
 * pill/line aspect rules; leather's `minSize`).
 */
import type { Vec2 } from "./Project";
import {
  boxToBoxTransformWithFlip,
  edgeMidpoints,
  isResizeEdge,
  resizeBox,
  resizeBoxEdge,
  resizeFlip,
  resizeFlipEdge,
  type Box,
  type ResizeCorner,
  type ResizeEdge,
  type ResizeHandle,
} from "./constructionResize";

/**
 * How far above the bbox top edge the rotation knob floats, in "screen px at
 * zoom 1" — call sites divide by the live zoom so the gap is constant on
 * screen at every zoom (#864, #971 follow-up). The drawn stem
 * (`buildTransformOverlay`) and both grab tests read this one value, so the
 * knob and the line it sits on move together.
 *
 * Raised from 24 after iPad testing (#40): the knob was inside the top corner
 * handles' grab zone, and on the construction side the corner is picked FIRST
 * (`ConstructionTool.onDown` runs resize before rotation), so a corner within
 * reach swallowed the knob outright. A corner grabs 7 px, and a finger scales
 * that by `COARSE_HIT_SCALE` to 28 — more than the 24 the knob used to hang
 * at, which is why it was hard to hit on a touch screen and fine with a mouse.
 * 40 clears that with room to spare on both sides.
 */
export const TRANSFORM_KNOB_OFFSET_MM = 40;

/** Rendered radius of the rotation knob. Matches the resize squares' half-size
 *  so the knob reads as big as a corner handle (#971 follow-up). Visual only —
 *  the grab is a separate geometric check with its own radius. */
export const TRANSFORM_KNOB_RADIUS_MM = 3.5;

/** Rendered half-side of the square corner + edge resize handles (#388, #863).
 *  A ~7 px square reads clearly and is comfortable to target. Visual only. */
export const TRANSFORM_RESIZE_HANDLE_HALF_MM = 3.5;

/** Every grabbable point on a transform frame except the pivot crosshair, which
 *  lives in WORLD space (it doesn't tilt with the object) and so is picked by
 *  the owning controller rather than here. */
export type TransformHandle = "knob" | ResizeHandle;

/** Grab tuning for one pick. The radii are deliberately per-caller rather than
 *  another shared constant: construction's resize squares sit among offset
 *  lines and neighbouring geometry and are tuned against that crowding, while
 *  leather's frame floats over a part. What must not differ — and no longer
 *  can — is the geometry the radii are measured against. */
export interface HandlePick {
  /** Live view zoom. Radii and the knob offset are "screen px at zoom 1", so
   *  they are divided by this to stay screen-constant (#864). */
  zoom: number;
  /** Grab radius for the rotation knob. */
  knobRadiusMm: number;
  /** Grab radius for the corner / edge squares. */
  resizeRadiusMm: number;
  /** Whether single-dimension EDGE handles are offered at all (#863). */
  allowEdgeResize: boolean;
}

/** Rotate `p` by `-deg` about `center` — maps a WORLD point into the un-rotated
 *  frame of an object tilted by `deg`, so axis-aligned box math just works. */
export function unrotatePoint(
  p: { x: number; y: number },
  center: { x: number; y: number },
  deg: number,
): Vec2 {
  return rotatePointAbout(p, center, -deg);
}

/** Rotate `p` by `deg` about `center` (degrees, y-down) — the forward of
 *  `unrotatePoint`. */
export function rotatePointAbout(
  p: { x: number; y: number },
  center: { x: number; y: number },
  deg: number,
): Vec2 {
  if (Math.abs(deg) < 1e-6) return { x: p.x, y: p.y };
  const rad = (deg * Math.PI) / 180;
  const c = Math.cos(rad);
  const s = Math.sin(rad);
  const dx = p.x - center.x;
  const dy = p.y - center.y;
  return { x: center.x + dx * c - dy * s, y: center.y + dx * s + dy * c };
}

/** Where the rotation knob sits, in the object's UN-ROTATED frame: centered over
 *  the top edge, `TRANSFORM_KNOB_OFFSET_MM / zoom` above it. */
export function rotationKnobPoint(box: Box, zoom: number): Vec2 {
  return {
    x: box.x + box.width / 2,
    y: box.y - TRANSFORM_KNOB_OFFSET_MM / zoom,
  };
}

/** True when `local` (the cursor already mapped into the un-rotated frame)
 *  grabs the rotation knob. */
export function isOnRotationKnob(
  box: Box,
  local: { x: number; y: number },
  opts: { zoom: number; radiusMm: number },
): boolean {
  const knob = rotationKnobPoint(box, opts.zoom);
  const r = opts.radiusMm / opts.zoom;
  return (local.x - knob.x) ** 2 + (local.y - knob.y) ** 2 <= r * r;
}

/**
 * Which axes of `box` can actually be resized (#1055).
 *
 * An axis with no extent has no scale to change: `boxToBoxTransform` guards a
 * zero original extent with a factor of 1, so an edge handle on the flat axis
 * of a straight seam would be a visible control that provably does nothing.
 * Per-AXIS rather than per-object-kind, because "seams don't get edge handles"
 * was the wrong shape of rule — a curved seam has extent in both directions and
 * should get all four, while a perfectly flat CUTOUT should get neither pair.
 */
export function resizableAxes(box: Box): { x: boolean; y: boolean } {
  // A millimetre epsilon rather than `> 0`: a straight seam's bounds come back
  // from paper with float noise on the flat axis, not an exact zero.
  const EPS_MM = 1e-6;
  return { x: box.width > EPS_MM, y: box.height > EPS_MM };
}

/** The edge handles worth offering on `box` — the pair that would scale an
 *  axis with real extent. */
export function offeredEdges(box: Box): ResizeEdge[] {
  const axes = resizableAxes(box);
  const out: ResizeEdge[] = [];
  // Left / right scale the WIDTH; top / bottom scale the HEIGHT.
  if (axes.x) out.push("l", "r");
  if (axes.y) out.push("t", "b");
  return out;
}

/** The four corner points of `box`, in the fixed pick order. */
function cornerPoints(box: Box): { key: ResizeCorner; x: number; y: number }[] {
  return [
    { key: "tl", x: box.x, y: box.y },
    { key: "tr", x: box.x + box.width, y: box.y },
    { key: "bl", x: box.x, y: box.y + box.height },
    { key: "br", x: box.x + box.width, y: box.y + box.height },
  ];
}

/**
 * Which resize handle `local` grabs, or null.
 *
 * Corners beat edges: on a thin box the two sit almost on top of each other,
 * and the corner (2-D scale) is the more capable gesture, so it wins outright
 * rather than by distance. Within a class the NEAREST wins, so overlapping
 * grab circles resolve to the one the user aimed at.
 */
export function pickResizeHandle(
  box: Box,
  local: { x: number; y: number },
  opts: { zoom: number; radiusMm: number; allowEdgeResize: boolean },
): ResizeHandle | null {
  const r = opts.radiusMm / opts.zoom;
  const r2 = r * r;
  const nearest = (
    pts: { key: ResizeHandle; x: number; y: number }[],
  ): ResizeHandle | null => {
    let best: { key: ResizeHandle; d: number } | null = null;
    for (const c of pts) {
      const d = (local.x - c.x) ** 2 + (local.y - c.y) ** 2;
      if (d <= r2 && (!best || d < best.d)) best = { key: c.key, d };
    }
    return best?.key ?? null;
  };
  const corner = nearest(cornerPoints(box));
  if (corner) return corner;
  if (!opts.allowEdgeResize) return null;
  const mids = edgeMidpoints(box);
  // Only the edges whose axis has extent (#1055) — the rest would grab and then
  // do nothing, which reads as a broken handle rather than an absent one.
  return nearest(offeredEdges(box).map((key) => ({ key, ...mids[key] })));
}

/** The full pick: rotation knob first (it sits clear of the box, so it can't
 *  steal a corner), then the resize handles. `local` is the cursor in the
 *  object's un-rotated frame. */
export function pickTransformHandle(
  box: Box,
  local: { x: number; y: number },
  opts: HandlePick,
): TransformHandle | null {
  if (
    isOnRotationKnob(box, local, {
      zoom: opts.zoom,
      radiusMm: opts.knobRadiusMm,
    })
  )
    return "knob";
  return pickResizeHandle(box, local, {
    zoom: opts.zoom,
    radiusMm: opts.resizeRadiusMm,
    allowEdgeResize: opts.allowEdgeResize,
  });
}

/** CSS cursor for a resize handle. Corners are diagonal (TL/BR ↘↖, TR/BL ↗↙);
 *  edges are axis-aligned (T/B ↕, L/R ↔). Kept un-rotated even for a tilted
 *  object — the base cursor still reads the intended axis. */
export function resizeCursorFor(handle: ResizeHandle): string {
  switch (handle) {
    case "tl":
    case "br":
      return "nwse-resize";
    case "tr":
    case "bl":
      return "nesw-resize";
    case "t":
    case "b":
      return "ns-resize";
    case "l":
    case "r":
      return "ew-resize";
  }
}

/**
 * The DISPLAYED center of a (possibly tilted) box — its un-rotated center
 * rotated by the baked tilt about the tilt's own pivot.
 *
 * This is what the user reads as "the middle of the box", and so the natural
 * rotation pivot when none has been parked (#420). Note it is NOT the baked
 * `rotation.center`: for a cutout or seam that inherited its tilt from the
 * owning part, that pivot is the PART's center — far away — and orbiting it
 * would swing the object in a wide arc instead of spinning it in place (#971).
 */
export function displayedBoxCenter(
  box: Box,
  rotation: { deg: number; center: { x: number; y: number } } | null,
): Vec2 {
  const c = { x: box.x + box.width / 2, y: box.y + box.height / 2 };
  if (!rotation) return c;
  return rotatePointAbout(c, rotation.center, rotation.deg);
}

/**
 * Map a world point through a resize of a (possibly tilted) box: out of the
 * tilt, `orig` → `next` (with axis flips), back into the tilt. The same map
 * the geometry takes, so a point carried through it keeps its place relative
 * to the object.
 */
export function mapPointThroughResize(
  p: Vec2,
  orig: Box,
  next: Box,
  flip: { x: boolean; y: boolean },
  rotation: { deg: number; center: { x: number; y: number } } | null,
): Vec2 {
  const t = boxToBoxTransformWithFlip(orig, next, flip);
  const local = rotation ? unrotatePoint(p, rotation.center, rotation.deg) : p;
  const scaled = { x: t.sx * local.x + t.tx, y: t.sy * local.y + t.ty };
  return rotation
    ? rotatePointAbout(scaled, rotation.center, rotation.deg)
    : scaled;
}

/**
 * Where the rotation pivot sits while a resize drag is in flight — one answer
 * for leather and construction alike (#1111).
 *
 * `orig` and `next` are measured in the un-rotated frame captured when the
 * drag STARTED, so everything maps through that same `pressRotation`. Reading
 * the rotation live from the resized preview mixed two frames: a resize carries
 * the rotation center along with the geometry, so the pivot drifted off at the
 * wrong angle and then snapped into place on release.
 *
 * With no parked pivot it is the displayed center of the new box. A parked
 * pivot travels with the object, through the same map as its geometry, so it
 * keeps its place relative to the object — and the committed resize maps it
 * the same way, so it stays there on release.
 */
export function resizeDragPivot(
  orig: Box,
  next: Box,
  flip: { x: boolean; y: boolean },
  pressRotation: { deg: number; center: { x: number; y: number } } | null,
  parked: Vec2 | null,
): Vec2 {
  return parked
    ? mapPointThroughResize(parked, orig, next, flip, pressRotation)
    : displayedBoxCenter(next, pressRotation);
}

/**
 * One frame of a resize drag: the grabbed `handle` follows `local` (the cursor
 * in the un-rotated frame) and out comes the new box plus which axes the drag
 * has flipped past their pinned reference.
 *
 * `aspectLock` only applies to corners — an edge drag is single-dimension by
 * definition, so `resizeBoxEdge` has no aspect notion. Callers layer their own
 * constraints on the result (construction pins a pill's short axis; leather
 * passes a `minSize` floor).
 */
export function applyResizeStep(
  orig: Box,
  handle: ResizeHandle,
  local: { x: number; y: number },
  opts: { aspectLock?: boolean; fromCenter?: boolean; minSize?: number } = {},
): { box: Box; flip: { x: boolean; y: boolean } } {
  const { aspectLock = false, fromCenter = false, minSize } = opts;
  if (isResizeEdge(handle)) {
    return {
      box: resizeBoxEdge(orig, handle, local, { fromCenter, minSize }),
      flip: resizeFlipEdge(orig, handle, local, fromCenter),
    };
  }
  return {
    box: resizeBox(orig, handle, local, { aspectLock, fromCenter, minSize }),
    flip: resizeFlip(orig, handle, local, fromCenter),
  };
}
